import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from "react";
import ReactDOM from "react-dom";
import { Button, Input, Typography, ConfigProvider, message, Tag } from "antd";
import {
  ScanOutlined,
  CheckCircleFilled,
  CloseOutlined,
  ReloadOutlined,
  UserDeleteOutlined,
  RetweetOutlined,
  BulbOutlined,
  BulbFilled,
  CameraOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import {
  getAPIRequest,
  putPostRequest,
} from "utilFunctions/Axios/AxiosRequestConfiguration";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import { UserInfoContext } from "Context/CommonContext";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";

const { Title, Text } = Typography;

type ScanTarget = "schedule" | "batch" | "absent" | null;
interface ScannerModalProps {
  open: boolean;
  onClose: () => void;
  onResult: (value: string) => void;
  fieldLabel: string;
  scannedPsidsRef?: React.MutableRefObject<string[]>;
  manualPsidsRef?: React.MutableRefObject<string[]>;
}

// ── Camera helpers ────────────────────────────────────────────────────────────
// Lenses that are NOT the main autofocus sensor — fixed-focus ultrawide / macro /
// depth lenses can't focus on a close barcode, so they must not be the default.
const NON_MAIN_LENS = /wide|ultra|tele|macro|depth|mono|tof|fisheye/i;

// MUST be called after getUserMedia() — labels are empty strings before permission.
// Returns the back-facing cameras with the main lens first, for the camera switcher
// (wide/ultra/etc are kept in the list so the user can still override to any lens).
async function listBackCameras(): Promise<MediaDeviceInfo[]> {
  const devices = await BrowserMultiFormatReader.listVideoInputDevices();
  const back = devices.filter((d) => /back|rear|environment/i.test(d.label));
  const pool = back.length ? back : devices;
  return [...pool].sort(
    (a, b) =>
      (NON_MAIN_LENS.test(a.label) ? 1 : 0) -
      (NON_MAIN_LENS.test(b.label) ? 1 : 0)
  );
}

const ScannerModal: React.FC<ScannerModalProps> = ({
  open,
  onClose,
  onResult,
  fieldLabel,
  scannedPsidsRef,
  manualPsidsRef,
}) => {
  const userInfo = useContext<any>(UserInfoContext);
  const [lastScanned, setLastScanned] = useState("");
  const [camError, setCamError] = useState("");
  const [isStarting, setIsStarting] = useState(false);

  // ── Camera capability controls ──────────────────────────────────────────────
  const [canSwitch, setCanSwitch] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomRange, setZoomRange] = useState<{
    min: number;
    max: number;
    step: number;
  } | null>(null);
  const [zoom, setZoom] = useState(0);
  const [captureMsg, setCaptureMsg] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<any>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const backCamerasRef = useRef<MediaDeviceInfo[]>([]);
  const cooldownRef = useRef(false);
  const lastScannedRef = useRef("");
  const audioCtxRef = useRef<AudioContext | null>(null);
  const nativeDetectActiveRef = useRef<boolean>(false);
  const nativeDetectorRef = useRef<any>(null);
  const captureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const isAbsent = fieldLabel === "Absent Student Barcode";
  const [glowActive, setGlowActive] = useState(false);

  // ── Beep ────────────────────────────────────────────────────────────────────
  const playBeep = useCallback(() => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.18);
    } catch (_) {
      //
    }
  }, []);

  // ── Stop ────────────────────────────────────────────────────────────────────
  const stopScanner = useCallback(() => {
    nativeDetectActiveRef.current = false;
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
    try {
      controlsRef.current?.stop();
    } catch (_) {
      //
    }
    controlsRef.current = null;
    trackRef.current = null;
  }, []);

  // ── Single decode handler — shared by live scan and tap-to-capture ────────────
  const handleDecodedText = useCallback(
    (rawText: string) => {
      const text = rawText?.trim();
      if (!text || cooldownRef.current) return;

      if (isAbsent && !/^\d{11}$/.test(text)) return;

      if (!isAbsent && !/^\d{6}$/.test(text)) return;

      const duplicate =
        isAbsent &&
        (scannedPsidsRef?.current?.includes(text) ||
          manualPsidsRef?.current?.includes(text));
      if (duplicate) return;
      if (isAbsent && text === lastScannedRef.current) return;

      cooldownRef.current = true;
      setTimeout(
        () => {
          cooldownRef.current = false;
        },
        isAbsent ? 800 : 1500
      );
      playBeep();

      if (isAbsent) {
        lastScannedRef.current = text;
        setLastScanned(text);
        onResult(text);
        setGlowActive(false);
        requestAnimationFrame(() => setGlowActive(true)); // restart animation each scan
        setTimeout(() => setGlowActive(false), 300);
      } else {
        onResult(text);
        stopScanner();
        onClose();
      }
    },
    [
      isAbsent,
      onResult,
      onClose,
      stopScanner,
      playBeep,
      scannedPsidsRef,
      manualPsidsRef,
    ]
  );

  // ── Start ───────────────────────────────────────────────────────────────────
  const startScanner = useCallback(async () => {
    setCamError("");
    setCaptureMsg("");
    setIsStarting(true);
    setTorchOn(false);
    stopScanner();

    try {
      // Step 1 — trigger permission prompt so device labels become readable
      const temp = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      temp.getTracks().forEach((t) => t.stop());

      // Get back cameras and find the best autofocus rear lens *before* launching the reader
      const backs = await listBackCameras();
      backCamerasRef.current = backs;
      setCanSwitch(backs.length > 1);

      let chosenId = localStorage.getItem("scannerDeviceId");
      if (!chosenId && backs.length > 0) {
        chosenId = backs[0].deviceId;
        localStorage.setItem("scannerDeviceId", chosenId);
      }

      // Step 2 — CODE 39 only, TRY_HARDER for mid-range phone cameras
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_39]);
      hints.set(DecodeHintType.TRY_HARDER, true);

      const reader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 800, // fast enough without hammering CPU
        delayBetweenScanSuccess: isAbsent ? 800 : 1500,
      });
      readerRef.current = reader;

      // Try using native BarcodeDetector if supported by the browser (very robust on older/low-end devices)
      if ("BarcodeDetector" in window) {
        try {
          const formats = await (
            window as any
          ).BarcodeDetector.getSupportedFormats();
          if (formats.includes("code_39")) {
            nativeDetectorRef.current = new (window as any).BarcodeDetector({
              formats: ["code_39"],
            });
          }
        } catch (e) {
          console.warn(
            "Failed to check or initialize native BarcodeDetector:",
            e
          );
        }
      }

      // Step 3 — high resolution + continuous focus. Let the OS pick the MAIN rear
      // lens via facingMode (far more reliable across phones than guessing a
      // deviceId). A user-chosen camera (from the switcher) is preferred but kept
      // NON-exact so a stale/invalid id can't hard-fail the stream.
      const videoConstraints: MediaTrackConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: "continuous" } as any],
      };
      if (chosenId) {
        (videoConstraints as any).deviceId = { ideal: chosenId };
      } else {
        (videoConstraints as any).facingMode = { ideal: "environment" };
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });
      videoRef.current!.srcObject = stream;
      await videoRef.current!.play();
      controlsRef.current = {
        stop: () => stream.getTracks().forEach((t) => t.stop()),
      };

      // Step 4 — inspect the live track: re-assert continuous focus (the
      // getUserMedia-time hint is unreliable) and surface zoom/torch controls
      // when the hardware supports them.
      const track =
        (
          videoRef.current?.srcObject as MediaStream | null
        )?.getVideoTracks?.()[0] ?? null;
      trackRef.current = track;
      const caps = (track?.getCapabilities?.() ?? {}) as any;

      if (
        track &&
        Array.isArray(caps.focusMode) &&
        caps.focusMode.includes("continuous")
      ) {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: "continuous" } as any],
          });
        } catch (_) {
          //
        }
      }

      // Zoom — enlarging the barcode is the strongest lever on fixed-focus lenses.
      if (track && caps.zoom && typeof caps.zoom.max === "number") {
        const range = {
          min: typeof caps.zoom.min === "number" ? caps.zoom.min : 1,
          max: caps.zoom.max,
          step: typeof caps.zoom.step === "number" ? caps.zoom.step : 0.1,
        };
        setZoomRange(range);

        // Default to a slight zoom (e.g. 1.2x or 12% of the range) if zoom is supported
        // This is a subtle zoom that improves performance on fixed-focus cameras by letting
        // users hold the device further back (where focus is sharp).
        const min = range.min;
        const max = range.max;
        const defaultZoom = Math.min(
          max,
          Math.max(min, min + (max - min) * 0.12)
        );

        const settings = (track.getSettings?.() ?? {}) as any;
        const initialZoom =
          typeof settings.zoom === "number" ? settings.zoom : defaultZoom;

        try {
          await track.applyConstraints({
            advanced: [{ zoom: initialZoom } as any],
          });
          setZoom(initialZoom);
        } catch (_) {
          setZoom(initialZoom);
        }
      } else {
        setZoomRange(null);
      }

      // Torch — boosts contrast, which indirectly helps focus and decoding.
      setTorchAvailable(!!track && "torch" in caps);

      // Start crop-based capture loop — this is the only decode path
      captureIntervalRef.current = setInterval(() => {
        captureFrame(false);
      }, 500);

      setIsStarting(false);
    } catch (err: any) {
      setIsStarting(false);
      if (err?.name === "NotAllowedError") {
        setCamError(
          "Camera permission denied. Allow access in browser settings."
        );
      } else if (err?.name === "NotFoundError") {
        setCamError("No camera found on this device.");
      } else {
        setCamError(err?.message || "Camera failed. Please tap Retry.");
      }
    }
  }, [isAbsent, stopScanner, handleDecodedText]);

  // ── Switch camera ─────────────────────────────────────────────────────────
  // Cycles through back-facing cameras and remembers the choice so it sticks
  // across sessions — the safety net for any phone where the auto-pick is wrong.
  const switchCamera = useCallback(() => {
    const cams = backCamerasRef.current;
    if (cams.length < 2) return;
    const currentId =
      ((trackRef.current?.getSettings?.() as any)?.deviceId as string) ||
      localStorage.getItem("scannerDeviceId") ||
      cams[0].deviceId;
    const idx = cams.findIndex((c) => c.deviceId === currentId);
    const next = cams[(idx + 1) % cams.length];
    localStorage.setItem("scannerDeviceId", next.deviceId);
    startScanner();
  }, [startScanner]);

  // ── Zoom / Torch ──────────────────────────────────────────────────────────
  const applyZoom = useCallback((value: number) => {
    const track = trackRef.current;
    if (!track) return;
    setZoom(value);
    track.applyConstraints({ advanced: [{ zoom: value } as any] }).catch(() => {
      // ignore error
    });
  }, []);

  const toggleTorch = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const next = !torchOn;
    track
      .applyConstraints({ advanced: [{ torch: next } as any] })
      .then(() => setTorchOn(next))
      .catch(() => {
        // ignore error
      });
  }, [torchOn]);

  // ── Tap-to-capture — single high-res still for phones that can't hold focus ──
  const captureFrame = useCallback(
    async (showFailMsg = true) => {
      const v = videoRef.current;
      const reader = readerRef.current;
      if (!v || !reader || !v.videoWidth) return;

      // 1. Get the actual rendered bounding rect of the video element
      const rect = v.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const displayW = rect.width;
      const displayH = rect.height;

      // 2. Calculate aspect ratios to handle 'object-fit: cover'
      const videoRatio = v.videoWidth / v.videoHeight;
      const displayRatio = displayW / displayH;

      let scale = 1;
      let xOffset = 0;
      let yOffset = 0;

      if (videoRatio > displayRatio) {
        // Video stream is wider than the element (cropped horizontally on screen)
        scale = v.videoHeight / displayH;
        xOffset = (v.videoWidth - displayW * scale) / 2;
      } else {
        // Video stream is taller than the element (cropped vertically on screen)
        scale = v.videoWidth / displayW;
        yOffset = (v.videoHeight - displayH * scale) / 2;
      }

      // 3. Define the UI Bounding Box dimensions matching your CSS precisely
      // Account for width: "85vw" and maxWidth: 400
      const viewportWidth = window.innerWidth;
      const computedBoxWidth = viewportWidth * 0.85;

      const boxWidthPx = Math.min(computedBoxWidth, 400);
      const boxHeightPx = isAbsent ? 140 : 100;

      // 4. Center of the screen in CSS Pixels (matching your centered Flex layout)
      const boxXInsideElement = (displayW - boxWidthPx) / 2;
      const boxYInsideElement = (displayH - boxHeightPx) / 2;

      // 5. Translate CSS layout coordinates to actual raw video stream pixels
      const cropX = xOffset + boxXInsideElement * scale;
      const cropY = yOffset + boxYInsideElement * scale;
      const cropW = boxWidthPx * scale;
      const cropH = boxHeightPx * scale;

      // Guard against sub-pixel rounding errors leaking outside stream boundaries
      const safeCropX = Math.max(0, cropX);
      const safeCropY = Math.max(0, cropY);
      const safeCropW = Math.min(v.videoWidth - safeCropX, cropW);
      const safeCropH = Math.min(v.videoHeight - safeCropY, cropH);

      const canvas = document.createElement("canvas");
      canvas.width = safeCropW;
      canvas.height = safeCropH;

      // ── ADD THIS CONSOLE LOG HERE ──
      console.log("📐 SCANNER DIMENSION COMPARISON:", {
        "1. Screen / Viewport Dimensions": {
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        },
        "2. Video HTML Element (CSS Pixels)": {
          elementWidth: displayW,
          elementHeight: displayH,
        },
        "3. Scan Box Overlay (What User Sees)": {
          boxWidthPx,
          boxHeightPx,
          boxXInsideElement,
          boxYInsideElement,
        },
        "4. Raw Camera Stream (Hardware Pixels)": {
          rawVideoWidth: v.videoWidth,
          rawVideoHeight: v.videoHeight,
          calculatedScaleFactor: scale,
        },
        "5. Final Cropped Frame (Sent to Canvas)": {
          canvasCropX: safeCropX,
          canvasCropY: safeCropY,
          canvasCropWidth: safeCropW,
          canvasCropHeight: safeCropH,
        },
      });

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(
        v,
        safeCropX,
        safeCropY,
        safeCropW,
        safeCropH, // Source (Camera feed)
        0,
        0,
        safeCropW,
        safeCropH // Destination (Canvas sizing)
      );

      // 1. Try native detector first as it is much better at decoding high-res images
      if (nativeDetectorRef.current) {
        try {
          const barcodes = await nativeDetectorRef.current.detect(canvas);
          if (barcodes && barcodes.length > 0) {
            const text = barcodes[0].rawValue;
            if (text) {
              setCaptureMsg("");
              handleDecodedText(text);
              return;
            }
          }
        } catch (err) {
          console.warn("Native capture detection failed:", err);
        }
      }

      // 2. Fall back to ZXing canvas reader
      try {
        const result = reader.decodeFromCanvas(canvas);
        setCaptureMsg("");
        handleDecodedText(result.getText());
      } catch (_) {
        if (showFailMsg) {
          setCaptureMsg("No barcode found — hold steady and try again.");
          setTimeout(() => setCaptureMsg(""), 2000);
        }
      }
    },
    [handleDecodedText]
  );

  // ── Lifecycle ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setLastScanned("");
    lastScannedRef.current = "";
    cooldownRef.current = false;
    try {
      audioCtxRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioCtxRef.current.resume();
    } catch (_) {
      //
    }
    startScanner();
    return () => {
      stopScanner();
    };
  }, [open]);

  useEffect(() => {
    pushToDataLayerForGTM({
      event: "ag_ops_view_offline_test_attendance",
      USER_ROLE: userInfo?.fexRole,
      EMPLOYEE_ID: userInfo?.employeeId,
    });
  }, []);

  const handleClose = useCallback(() => {
    stopScanner();
    setLastScanned("");
    setCamError("");
    setCaptureMsg("");
    setTorchOn(false);
    setZoomRange(null);
    onClose();
  }, [onClose, stopScanner]);

  const controlBtnStyle = (active: boolean): React.CSSProperties => ({
    width: 48,
    height: 48,
    borderRadius: "50%",
    border: active ? "none" : "1px solid rgba(255,255,255,0.4)",
    background: active ? "#00d4ff" : "rgba(0,0,0,0.55)",
    color: active ? "#001018" : "#fff",
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  });

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <video
        ref={videoRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        muted
        playsInline
        autoPlay
      />

      {/* Error */}
      {camError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "#000",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            gap: 16,
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: 48 }}>📷</span>
          <span
            style={{
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: 1.5,
            }}
          >
            {camError}
          </span>
          <button
            onClick={startScanner}
            style={{
              color: "#00d4ff",
              background: "none",
              border: "1px solid #00d4ff",
              borderRadius: 8,
              padding: "10px 28px",
              fontSize: 14,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Retry
          </button>
          <button
            onClick={handleClose}
            style={{
              color: "#fff",
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 8,
              padding: "10px 28px",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      )}

      {/* Loading */}
      {isStarting && !camError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 15,
            background: "#000",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid rgba(255,255,255,0.2)",
              borderTop: "3px solid #00d4ff",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: 14,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Starting camera...
          </span>
        </div>
      )}

      {/* Top bar */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "16px 20px",
          background: "rgba(0,0,0,0.7)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flex: 1,
          }}
        >
          {/* <button
          onClick={handleClose}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: "50%",
            width: 36,
            height: 36,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          <CloseOutlined />
        </button> */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <span
              style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 16,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Scan {fieldLabel}
            </span>
            {/* Right-aligned container — class added for responsive layout shifting */}
            {isAbsent && lastScanned && (
              <div style={{ display: "flex" }}>
                <span
                  style={{
                    background: "#2c7c1c",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "2px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                    transition: "box-shadow 0.1s ease",
                    boxShadow: glowActive
                      ? "0 0 0 3px #4ade80, 0 0 16px 4px #16a34a"
                      : "none",
                  }}
                >
                  Last: {lastScanned}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleClose}
          style={{
            padding: "5px 14px",
            borderRadius: "20px",
            background: "#ef4444",
            color: "#fff",
            border: "none",
            fontSize: "13px",
            fontWeight: "bold",
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
          }}
          title="Stop Scan"
        >
          Stop Scan
        </button>
      </div>

      {/* Camera controls — each shown only when the hardware supports it */}
      {!isStarting && !camError && (
        <div
          style={{
            position: "absolute",
            bottom: 250,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 12,
            display: "flex",
            flexDirection: "row",
            gap: 14,
            alignItems: "center",
          }}
        >
          {torchAvailable && (
            <button
              onClick={toggleTorch}
              style={controlBtnStyle(torchOn)}
              title="Torch"
            >
              {torchOn ? <BulbFilled /> : <BulbOutlined />}
            </button>
          )}
          {canSwitch && (
            <button
              onClick={switchCamera}
              style={controlBtnStyle(false)}
              title="Switch camera"
            >
              <span
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CameraOutlined style={{ fontSize: 18 }} />
                <RetweetOutlined
                  style={{
                    fontSize: 10,
                    position: "absolute",
                    bottom: -4,
                    right: -8,
                  }}
                />
              </span>
            </button>
          )}
          {zoomRange && (
            <>
              <button
                onClick={() =>
                  applyZoom(
                    Math.min(
                      zoomRange.max,
                      zoom + (zoomRange.max - zoomRange.min) / 8
                    )
                  )
                }
                style={controlBtnStyle(false)}
                title="Zoom in"
              >
                <PlusOutlined />
              </button>
              <button
                onClick={() =>
                  applyZoom(
                    Math.max(
                      zoomRange.min,
                      zoom - (zoomRange.max - zoomRange.min) / 8
                    )
                  )
                }
                style={controlBtnStyle(false)}
                title="Zoom out"
              >
                <MinusOutlined />
              </button>
            </>
          )}
        </div>
      )}

      <div
        style={{
          flex: 1,
          position: "relative",
          zIndex: 5,
          background: "rgba(0,0,0,0.75)",
        }}
      />

      {/* Scan box */}
      <div
        style={{
          position: "relative",
          zIndex: 5,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "rgba(0,0,0,0.75)",
            height: isAbsent ? 140 : 100,
          }}
        />
        <div
          style={{
            width: "85vw",
            maxWidth: 400,
            height: isAbsent ? 140 : 100,
            position: "relative",
            flexShrink: 0,
          }}
        >
          {[
            {
              top: 0,
              left: 0,
              borderTop: "3px solid #00d4ff",
              borderLeft: "3px solid #00d4ff",
              borderRadius: "4px 0 0 0",
            },
            {
              top: 0,
              right: 0,
              borderTop: "3px solid #00d4ff",
              borderRight: "3px solid #00d4ff",
              borderRadius: "0 4px 0 0",
            },
            {
              bottom: 0,
              left: 0,
              borderBottom: "3px solid #00d4ff",
              borderLeft: "3px solid #00d4ff",
              borderRadius: "0 0 0 4px",
            },
            {
              bottom: 0,
              right: 0,
              borderBottom: "3px solid #00d4ff",
              borderRight: "3px solid #00d4ff",
              borderRadius: "0 0 4px 0",
            },
          ].map((s, i) => (
            <div
              key={i}
              style={{ position: "absolute", width: 22, height: 22, ...s }}
            />
          ))}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: 2,
              background:
                "linear-gradient(90deg, transparent 0%, #00d4ff 40%, #fff 50%, #00d4ff 60%, transparent 100%)",
              animation: "barcodeScan 1.6s ease-in-out infinite",
              top: 0,
            }}
          />
        </div>
        <div
          style={{
            flex: 1,
            background: "rgba(0,0,0,0.75)",
            height: isAbsent ? 140 : 100,
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          position: "relative",
          zIndex: 5,
          background: "rgba(0,0,0,0.75)",
        }}
      />

      {/* Bottom hint */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          textAlign: "center",
          padding: "16px 24px 32px",
          background: "rgba(0,0,0,0.7)",
        }}
      >
        {/* Tap-to-capture — grabs one high-res still for phones that can't
            hold a live focus on the barcode */}
        {!isStarting && !camError && (
          <button
            onClick={() => captureFrame(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              margin: "0 auto 14px",
              padding: "10px 22px",
              borderRadius: 24,
              border: "1px solid rgba(255,255,255,0.4)",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              cursor: "pointer",
            }}
          >
            <CameraOutlined style={{ fontSize: 16 }} />
            Tap to capture
          </button>
        )}
        {captureMsg && (
          <div
            style={{
              color: "#fca5a5",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif",
              marginBottom: 10,
            }}
          >
            {captureMsg}
          </div>
        )}
        <div
          style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
            fontFamily: "'DM Sans', sans-serif",
            marginBottom: 4,
          }}
        >
          {isAbsent
            ? "Keep scanning — auto-captures each barcode"
            : "Point camera at barcode"}
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: 13,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Move the phone slowly nearer/further until the barcode looks sharp
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes barcodeScan {
          0%   { top: 0%;   opacity: 0.4; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0.4; }
        }
        @keyframes scanGlow {
           0%   { box-shadow: 0 0 0 3px #4ade80, 0 0 16px 4px #16a34a; }
           100% { box-shadow: none; }
        }
      `}</style>
    </div>,
    document.body
  );
};

// ── Section Label ──────────────────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      background: "#f8fafc",
      borderBottom: "1px solid #e8ecf0",
      padding: "8px 16px",
    }}
  >
    <Text
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#94a3b8",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Text>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const TestAttendance: React.FC = () => {
  const userInfo = useContext<any>(UserInfoContext);
  const [scheduleId, setScheduleId] = useState<string>("");
  const [batchId, setBatchId] = useState<string>("");
  const [scheduleIdInputType, setScheduleIdInputType] = useState<
    "SCAN" | "MANUAL"
  >("MANUAL");
  const [batchIdInputType, setBatchIdInputType] = useState<"SCAN" | "MANUAL">(
    "MANUAL"
  );
  const [sessionId, setSessionId] = useState<string>("");
  const [scanId, setScanId] = useState<string>("");
  const [scanTarget, setScanTarget] = useState<ScanTarget>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  // ── Separate scanned vs manual absentees ──────────────────────────────────
  const [scannedAbsentees, setScannedAbsentees] = useState<string[]>([]);
  const [manualAbsentees, setManualAbsentees] = useState<string[]>([]);
  const scannedAbsenteesRef = React.useRef<string[]>([]);
  const manualAbsenteesRef = React.useRef<string[]>([]);
  const [noAbsentee, setNoAbsentee] = useState<boolean>(false);

  // Combined for display only
  const absentees = [...scannedAbsentees, ...manualAbsentees];

  const [manualPsid, setManualPsid] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const { screen }: any = useWindowSize();
  const isLargeScreen: boolean = ["xl", "xxl", "lg"].includes(screen);
  const pagePadding: string = isLargeScreen
    ? "20px 20px 32px 48px"
    : "16px 16px 32px";

  const saveToLocalStorage = useCallback(
    (scanned: string[], manual: string[]) => {
      if (!scheduleId.trim() || !batchId.trim()) return;
      const storageKey = `attendance_${scheduleId.trim()}_${batchId.trim()}`;
      localStorage.setItem(storageKey, JSON.stringify({ scanned, manual }));
    },
    [scheduleId, batchId]
  );

  const SIX_DIGIT_ID = /^\d{6}$/;

  // ── Scan result handler ───────────────────────────────────────────────────
  const handleScanResult = useCallback(
    (value: string): void => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (scanTarget === "schedule") {
        if (!SIX_DIGIT_ID.test(trimmed)) {
          messageApi.error(
            "Test Schedule ID must be exactly 6 digits (numbers only)."
          );
          return;
        }
        setScheduleId(trimmed);
        setScheduleIdInputType("SCAN");
        messageApi.success({
          content: "Test Schedule ID scanned!",
          icon: <CheckCircleFilled style={{ color: "#00b4d8" }} />,
        });
        setScanTarget(null);
      } else if (scanTarget === "batch") {
        if (!SIX_DIGIT_ID.test(trimmed)) {
          messageApi.error("Batch ID must be exactly 6 digits (numbers only).");
          return;
        }
        setBatchId(trimmed);
        setBatchIdInputType("SCAN");
        messageApi.success({
          content: "Batch ID scanned!",
          icon: <CheckCircleFilled style={{ color: "#00b4d8" }} />,
        });
        setScanTarget(null);
      } else if (scanTarget === "absent") {
        if (noAbsentee) {
          messageApi.warning(
            "No Absentee is selected — clear it to scan barcodes."
          );
          setScanTarget(null);
          return;
        }
        if (!/^\d{11}$/.test(trimmed)) {
          messageApi.warning(
            "Invalid barcode — PSID must be an 11-digit number."
          );
          return;
        }
        const alreadyExists =
          scannedAbsenteesRef.current.includes(trimmed) ||
          manualAbsenteesRef.current.includes(trimmed);

        if (alreadyExists) {
          messageApi.warning(`PSID ${trimmed} already marked absent.`);
        } else {
          scannedAbsenteesRef.current = [
            ...scannedAbsenteesRef.current,
            trimmed,
          ].sort();
          setScannedAbsentees([...scannedAbsenteesRef.current]);
          saveToLocalStorage(
            scannedAbsenteesRef.current,
            manualAbsenteesRef.current
          );
          messageApi.success({
            content: `Marked absent: ${trimmed}`,
            icon: <CheckCircleFilled style={{ color: "#ef4444" }} />,
          });
        }
        // No setScanTarget — modal stays open for continuous scanning
      }
    },
    [scanTarget, messageApi]
  );

  //--------- device info --------------------------------------------
  const getDeviceInfo = (): string => {
    // Directly returns the platform string provided natively by the browser
    return (
      (navigator as any).userAgentData?.platform ||
      navigator.platform ||
      "Unknown Device"
    );
  };

  // ── Verify ────────────────────────────────────────────────────────────────
  const handleVerify = (): void => {
    if (!scheduleId.trim()) {
      messageApi.error("Please enter or scan the Test Schedule ID.");
      return;
    }
    if (!SIX_DIGIT_ID.test(scheduleId.trim())) {
      messageApi.error(
        "Test Schedule ID must be exactly 6 digits (numbers only)."
      );
      return;
    }
    if (!batchId.trim()) {
      messageApi.error("Please enter or scan the Batch ID.");
      return;
    }
    if (!SIX_DIGIT_ID.test(batchId.trim())) {
      messageApi.error("Batch ID must be exactly 6 digits (numbers only).");
      return;
    }
    setLoading(true);

    const generateUniqueId = (): string => {
      try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
          return crypto.randomUUID();
        }
      } catch (e) {
        // ignore and fallback
      }
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    const newSessionId = generateUniqueId();
    const newScanId = generateUniqueId();
    setSessionId(newSessionId);
    setScanId(newScanId);

    //Using your custom putPostRequest wrapper to hit your validation API
    const urlWithParams = `${BASE_URL}attendace/validateBatchAndTestScheduleId?batchId=${encodeURIComponent(
      batchId.trim()
    )}&testScheduleId=${encodeURIComponent(scheduleId.trim())}`;

    getAPIRequest({
      url: urlWithParams,
      success: (response: any) => {
        setLoading(false);
        console.log("validate response", response.data.data.data);
        // Checking the structure based on your provided JSON response: response.data.data === true
        if (response?.data?.data?.data === true) {
          // Generate IDs and update state only if backend confirms validity
          const newSessionId = generateUniqueId();
          const newScanId = generateUniqueId();
          setSessionId(newSessionId);
          setScanId(newScanId);

          setVerified(true);
          messageApi.success("Verification successful!");
          pushToDataLayerForGTM({
            event: "ag_ops_verified_attendance_attempt",
            USER_ROLE: userInfo?.fexRole,
            EMPLOYEE_ID: userInfo?.employeeId,
            TEST_SCHEDULE_ID: scheduleId.trim(),
            OFFLINE_BATCH_ID: batchId.trim(),
            SCAN_MANUAL:
              scheduleIdInputType === "SCAN" && batchIdInputType === "SCAN"
                ? "SCAN"
                : "MANUAL",
          });
          // ── Restore saved data from localStorage if exists ──
          const storageKey = `attendance_${scheduleId.trim()}_${batchId.trim()}`;
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            try {
              const { scanned, manual } = JSON.parse(saved);
              scannedAbsenteesRef.current = scanned || [];
              manualAbsenteesRef.current = manual || [];
              setScannedAbsentees([...scannedAbsenteesRef.current]);
              setManualAbsentees([...manualAbsenteesRef.current]);
              messageApi.info("Restored previous session data.");
            } catch (_) {
              // ignore corrupted data
            }
          }
        } else {
          // Handles cases where the API returns 200 OK but data is false or invalid
          messageApi.error("Invalid Schedule or Batch ID.");
        }
      },
      failure: (error: any) => {
        setLoading(false);
        messageApi.error("Verification failed. Please check the IDs provided.");
        console.error("Verification error:", error);
      },
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmitAttendance = (): void => {
    setSubmitting(true);

    const payload = {
      sessionId,
      scanId,
      testScheduleId: scheduleId,
      batchId: batchId,
      scannedPSID: scannedAbsenteesRef.current,
      manualPSID: manualAbsenteesRef.current,
      deviceInfo: getDeviceInfo(),
      noAbsentee,
    };

    console.log("Attendance payload:", payload);

    // Replace with your actual API call:
    // Using your custom putPostRequest wrapper
    putPostRequest({
      url: BASE_URL + "attendace/uploadAttendanceList",
      method: "POST", // assuming method expects "POST" based on your interface
      body: payload,
      success: () => {
        const storageKey = `attendance_${scheduleId.trim()}_${batchId.trim()}`;
        localStorage.removeItem(storageKey);
        setSubmitting(false);
        setSubmitted(true);
        // You can also utilize 'data' here if needed
        pushToDataLayerForGTM({
          event: "ag_ops_offline_test_attendance_submitted",
          USER_ROLE: userInfo?.fexRole,
          EMPLOYEE_ID: userInfo?.employeeId,
          TEST_SCHEDULE_ID: scheduleId,
          OFFLINE_BATCH_ID: batchId,
          COUNT:
            scannedAbsenteesRef.current.length +
            manualAbsenteesRef.current.length,
        });
      },
      failure: (_error: any) => {
        setSubmitting(false);
        const apiMessage =
          _error?.response?.data?.message ||
          _error?.data?.message ||
          _error?.message;
        messageApi.error(apiMessage || "Submission failed.");
        console.error("API Error:", _error);
      },
    });
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = (): void => {
    const storageKey = `attendance_${scheduleId.trim()}_${batchId.trim()}`;
    localStorage.removeItem(storageKey);
    setScheduleId("");
    setBatchId("");
    setVerified(false);
    setSubmitted(false);
    scannedAbsenteesRef.current = [];
    manualAbsenteesRef.current = [];
    setScannedAbsentees([]);
    setManualAbsentees([]);
    setManualPsid("");
    setSessionId("");
    setScanId("");
    setNoAbsentee(false);
  };

  // ── Remove absentee ───────────────────────────────────────────────────────
  const removeAbsentee = (psid: string): void => {
    scannedAbsenteesRef.current = scannedAbsenteesRef.current.filter(
      (p) => p !== psid
    );
    manualAbsenteesRef.current = manualAbsenteesRef.current.filter(
      (p) => p !== psid
    );
    setScannedAbsentees([...scannedAbsenteesRef.current]);
    setManualAbsentees([...manualAbsenteesRef.current]);
    saveToLocalStorage(scannedAbsenteesRef.current, manualAbsenteesRef.current);
  };

  // ── Manual add ────────────────────────────────────────────────────────────
  const handleManualAdd = (): void => {
    if (noAbsentee) {
      messageApi.warning("No Absentee is selected — clear it to add PSIDs.");
      return;
    }
    const trimmed = manualPsid.trim();
    if (!trimmed) {
      messageApi.error("Please enter a PSID.");
      return;
    }

    if (!/^\d{11}$/.test(trimmed)) {
      messageApi.error("PSID must be exactly 11 digits (numbers only).");
      return;
    }

    const alreadyExists =
      scannedAbsenteesRef.current.includes(trimmed) ||
      manualAbsenteesRef.current.includes(trimmed);
    if (alreadyExists) {
      messageApi.warning(`PSID ${trimmed} already marked absent.`);
      return;
    }
    manualAbsenteesRef.current = [
      ...manualAbsenteesRef.current,
      trimmed,
    ].sort();
    setManualAbsentees([...manualAbsenteesRef.current]);
    saveToLocalStorage(scannedAbsenteesRef.current, manualAbsenteesRef.current);
    setManualPsid("");
    messageApi.success({
      content: `Marked absent: ${trimmed}`,
      icon: <CheckCircleFilled style={{ color: "#ef4444" }} />,
    });
  };

  const scannerFieldLabel: string =
    scanTarget === "schedule"
      ? "Test Schedule ID"
      : scanTarget === "batch"
      ? "Batch ID"
      : "Absent Student Barcode";

  const verifiedInputStyle: React.CSSProperties = {
    flex: 1,
    borderColor: "#86efac",
    borderRadius: 10,
    fontSize: 14,
    height: 50,
    color: "#166534",
    background: "#f0fdf4",
  };
  const normalInputStyle: React.CSSProperties = {
    flex: 1,
    borderColor: "#d1dbe8",
    borderRadius: 10,
    fontSize: 14,
    height: 50,
    color: "#1e293b",
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#00b4d8",
          borderRadius: 8,
          fontFamily: "'DM Sans', sans-serif",
        },
      }}
    >
      <>
        {contextHolder}

        {/* Submitting loader */}
        {submitting &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99998,
                background: "rgba(255,255,255,0.88)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #00b4d8",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <span
                style={{
                  color: "#64748b",
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Submitting attendance...
              </span>
              <style>
                {"@keyframes spin { to { transform: rotate(360deg); } }"}
              </style>
            </div>,
            document.body
          )}

        {/* ── SUCCESS PAGE ── */}
        {submitted && (
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              padding: isLargeScreen ? "40px 20px 32px 48px" : "32px 16px",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "#dcfce7",
                  border: "2px solid #bbf7d0",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                <CheckCircleFilled style={{ fontSize: 36, color: "#16a34a" }} />
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#1a1a2e",
                  marginBottom: 8,
                }}
              >
                Submitted!
              </div>
              <div style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>
                Attendance for{" "}
                <strong>
                  Schedule {scheduleId} / Batch {batchId}
                </strong>{" "}
                recorded.{" "}
                {noAbsentee
                  ? "All students auto-marked present."
                  : "All other students auto-marked present."}
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#00b4d8",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <span>⚡</span> Synced to QuickSight
              </div>
            </div>

            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div
                style={{
                  flex: 1,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  padding: "20px 0",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    color: "#ef4444",
                    lineHeight: 1,
                  }}
                >
                  {absentees.length}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    letterSpacing: "0.08em",
                    marginTop: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Absent
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  padding: "20px 0",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    color: "#16a34a",
                    lineHeight: 1,
                  }}
                >
                  Auto
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    letterSpacing: "0.08em",
                    marginTop: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Present
                </div>
              </div>
            </div>

            {absentees.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <SectionLabel>Absent PSIDs</SectionLabel>
                <div
                  style={{
                    padding: "14px 20px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                  }}
                >
                  {absentees.map((psid, idx) => (
                    <span
                      key={psid}
                      style={{
                        fontSize: 14,
                        color: "#1e293b",
                        fontWeight: 600,
                      }}
                    >
                      {psid}
                      {idx < absentees.length - 1 && (
                        <span style={{ color: "#94a3b8", margin: "0 6px" }}>
                          ·
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleReset}
              style={{
                width: "100%",
                height: 54,
                background: "linear-gradient(90deg, #00b4d8 0%, #0096c7 100%)",
                border: "none",
                borderRadius: 12,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 4px 16px rgba(0,180,216,0.3)",
              }}
            >
              <ReloadOutlined style={{ color: "#fff", fontSize: 16 }} />
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                Start New Session
              </span>
            </button>
          </div>
        )}

        {/* ── MAIN FORM ── */}
        {!submitted && (
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              padding: pagePadding,
              background: "#f1f5f9",
              minHeight: "100vh",
            }}
          >
            <div style={{ marginBottom: 20 }}>
              <Title
                level={2}
                style={{
                  margin: 0,
                  fontWeight: 800,
                  color: "#1a1a2e",
                  fontSize: 22,
                }}
              >
                Test Attendance
              </Title>
              <Text
                style={{
                  color: "#6b7280",
                  fontSize: 13,
                  marginTop: 2,
                  display: "block",
                }}
              >
                Scan or enter Test Schedule ID and Batch ID
              </Text>
            </div>

            {/* Session Details Card */}
            <div
              style={{
                width: "100%",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                overflow: "hidden",
                marginBottom: 16,
              }}
            >
              <SectionLabel>Session Details</SectionLabel>
              <div style={{ padding: "16px 16px 20px" }}>
                {/* Test Schedule ID */}
                <div style={{ marginBottom: 24 }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: 14,
                      marginBottom: 8,
                    }}
                  >
                    Test Schedule ID <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Input
                      value={scheduleId}
                      onChange={(e) => {
                        if (!verified) {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6); // ── only digits, max 6
                          setScheduleId(val);
                          setScheduleIdInputType("MANUAL");
                        }
                      }}
                      placeholder="Scan or type Test Schedule ID"
                      size="large"
                      style={verified ? verifiedInputStyle : normalInputStyle}
                      readOnly={verified}
                      allowClear={!verified}
                    />
                    <Button
                      size="large"
                      onClick={() => {
                        if (!verified) setScanTarget("schedule");
                      }}
                      disabled={verified}
                      style={{
                        height: 50,
                        width: 50,
                        borderRadius: 10,
                        border: `1.5px solid ${
                          verified ? "#86efac" : "#00b4d8"
                        }`,
                        color: verified ? "#86efac" : "#00b4d8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        flexShrink: 0,
                      }}
                      icon={
                        verified ? (
                          <CheckCircleFilled style={{ fontSize: 20 }} />
                        ) : (
                          <ScanOutlined style={{ fontSize: 20 }} />
                        )
                      }
                    />
                  </div>
                  {verified ? (
                    <Text
                      style={{
                        fontSize: 12,
                        color: "#16a34a",
                        marginTop: 6,
                        display: "block",
                        fontWeight: 600,
                      }}
                    >
                      ✓ Verified
                    </Text>
                  ) : (
                    <Text
                      style={{
                        fontSize: 12,
                        color: "#94a3b8",
                        marginTop: 6,
                        display: "block",
                      }}
                    >
                      Enter the ID printed at the top of the attendance sheet
                    </Text>
                  )}
                </div>

                {/* Batch ID */}
                <div style={{ marginBottom: verified ? 0 : 28 }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: 14,
                      marginBottom: 8,
                    }}
                  >
                    Batch ID <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Input
                      value={batchId}
                      onChange={(e) => {
                        if (!verified) {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6); // ── only digits, max 6
                          setBatchId(val);
                          setBatchIdInputType("MANUAL");
                        }
                      }}
                      placeholder="Scan or type Batch ID"
                      size="large"
                      style={verified ? verifiedInputStyle : normalInputStyle}
                      readOnly={verified}
                      allowClear={!verified}
                    />
                    <Button
                      size="large"
                      onClick={() => {
                        if (!verified) setScanTarget("batch");
                      }}
                      disabled={verified}
                      style={{
                        height: 50,
                        width: 50,
                        borderRadius: 10,
                        border: `1.5px solid ${
                          verified ? "#86efac" : "#00b4d8"
                        }`,
                        color: verified ? "#86efac" : "#00b4d8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        flexShrink: 0,
                      }}
                      icon={
                        verified ? (
                          <CheckCircleFilled style={{ fontSize: 20 }} />
                        ) : (
                          <ScanOutlined style={{ fontSize: 20 }} />
                        )
                      }
                    />
                  </div>
                  {verified ? (
                    <Text
                      style={{
                        fontSize: 12,
                        color: "#16a34a",
                        marginTop: 6,
                        display: "block",
                        fontWeight: 600,
                      }}
                    >
                      ✓ Verified
                    </Text>
                  ) : (
                    <Text
                      style={{
                        fontSize: 12,
                        color: "#94a3b8",
                        marginTop: 6,
                        display: "block",
                      }}
                    >
                      Enter the Batch ID printed at the top of the attendance
                      sheet
                    </Text>
                  )}
                </div>

                {!verified && (
                  <div style={{ marginTop: 28 }}>
                    <Button
                      type="primary"
                      size="large"
                      loading={loading}
                      onClick={handleVerify}
                      block
                      style={{
                        height: 54,
                        borderRadius: 12,
                        background:
                          "linear-gradient(90deg, #00b4d8 0%, #0096c7 100%)",
                        border: "none",
                        fontWeight: 700,
                        fontSize: 16,
                        boxShadow: "0 4px 16px rgba(0,180,216,0.35)",
                      }}
                    >
                      {loading ? "Verifying..." : "Verify & Start Attendance →"}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Absentees section */}
            {verified && (
              <div
                style={{
                  width: "100%",
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <SectionLabel>Absentees</SectionLabel>
                <div
                  style={{
                    background:
                      "linear-gradient(90deg, #00b4d8 0%, #0096c7 100%)",
                    padding: "10px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tag
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        color: "#fff",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 10px",
                      }}
                    >
                      Schedule: {scheduleId}
                    </Tag>
                    <Tag
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        color: "#fff",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 10px",
                      }}
                    >
                      Batch: {batchId}
                    </Tag>
                  </div>
                  <Button
                    size="small"
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(255,255,255,0.4)",
                      color: "#fff",
                      borderRadius: 6,
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    ↩ Reset
                  </Button>
                </div>
                {/* Counters */}
                <div
                  style={{ display: "flex", borderBottom: "1px solid #e8ecf0" }}
                >
                  <div
                    style={{
                      flex: 1,
                      padding: "18px 0",
                      textAlign: "center",
                      borderRight: "1px solid #e8ecf0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: "#ef4444",
                        lineHeight: 1,
                      }}
                    >
                      {absentees.length}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#94a3b8",
                        letterSpacing: "0.08em",
                        marginTop: 4,
                        textTransform: "uppercase",
                      }}
                    >
                      Absent
                    </div>
                  </div>
                  <div
                    style={{ flex: 1, padding: "18px 0", textAlign: "center" }}
                  >
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: "#00b4d8",
                        lineHeight: 1,
                      }}
                    >
                      {scannedAbsentees.length}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#94a3b8",
                        letterSpacing: "0.08em",
                        marginTop: 4,
                        textTransform: "uppercase",
                      }}
                    >
                      Scanned
                    </div>
                  </div>
                </div>
                {/* Scan button */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e8ecf0",
                  }}
                >
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 7, minWidth: 0 }}>
                      <Button
                        type="primary"
                        size="large"
                        block
                        onClick={() => {
                          if (!noAbsentee) setScanTarget("absent");
                        }}
                        disabled={noAbsentee}
                        icon={
                          <ScanOutlined
                            style={{
                              fontSize: 18,
                              color: noAbsentee ? "#94a3b8" : undefined,
                            }}
                          />
                        }
                        style={{
                          height: 50,
                          borderRadius: 10,
                          background: noAbsentee
                            ? "#e2e8f0"
                            : "linear-gradient(90deg, #00b4d8 0%, #0096c7 100%)",
                          border: "none",
                          color: noAbsentee ? "#94a3b8" : "#fff",
                          fontWeight: 700,
                          fontSize: 15,
                          boxShadow: noAbsentee
                            ? "none"
                            : "0 4px 16px rgba(0,180,216,0.3)",
                        }}
                      >
                        Scan Absent Barcodes
                      </Button>
                    </div>
                    <div style={{ flex: 3, minWidth: 0 }}>
                      <Button
                        size="large"
                        block
                        onClick={() => setNoAbsentee((prev) => !prev)}
                        disabled={absentees.length > 0}
                        icon={noAbsentee ? <CheckCircleFilled /> : undefined}
                        style={{
                          height: 50,
                          borderRadius: 10,
                          border: `1.5px solid ${
                            absentees.length > 0 ? "#e2e8f0" : "#00b4d8"
                          }`,
                          background: noAbsentee ? "#e0f7fb" : "#fff",
                          color:
                            absentees.length > 0
                              ? "#cbd5e1"
                              : noAbsentee
                              ? "#0096c7"
                              : "#00b4d8",
                          fontWeight: 700,
                          fontSize: 13,
                          paddingInline: 6,
                          whiteSpace: "normal",
                          lineHeight: 1.2,
                        }}
                      >
                        No Absentee
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Manual input */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e8ecf0",
                    display: "flex",
                    gap: 10,
                  }}
                >
                  <Input
                    value={manualPsid}
                    onChange={(e) => setManualPsid(e.target.value)}
                    onPressEnter={handleManualAdd}
                    placeholder="Manually add 11-digit PSID"
                    size="large"
                    disabled={noAbsentee}
                    style={{
                      flex: 1,
                      borderRadius: 10,
                      height: 46,
                      fontSize: 14,
                    }}
                    maxLength={11}
                  />
                  <Button
                    size="large"
                    onClick={handleManualAdd}
                    style={{
                      height: 46,
                      borderRadius: 10,
                      border: "1.5px solid #00b4d8",
                      color: "#00b4d8",
                      fontWeight: 700,
                      paddingInline: 20,
                    }}
                  >
                    + Add
                  </Button>
                </div>
                {/* Marked Absent list */}
                <div style={{ padding: "0 0 16px" }}>
                  <div
                    style={{
                      padding: "12px 20px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#94a3b8",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      Marked Absent
                    </Text>
                    {absentees.length > 0 && (
                      <span
                        style={{
                          background: "#ef4444",
                          color: "#fff",
                          borderRadius: "50%",
                          width: 20,
                          height: 20,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {absentees.length}
                      </span>
                    )}
                  </div>
                  {absentees.length === 0 ? (
                    <div style={{ padding: "32px 0", textAlign: "center" }}>
                      <UserDeleteOutlined
                        style={{
                          fontSize: 36,
                          color: "#d1d5db",
                          display: "block",
                          marginBottom: 8,
                        }}
                      />
                      <Text style={{ color: "#9ca3af", fontSize: 13 }}>
                        No absentees yet
                      </Text>
                    </div>
                  ) : (
                    <div style={{ padding: "0 20px" }}>
                      {absentees.map((psid, idx) => (
                        <div
                          key={psid}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #f1f5f9",
                            padding: "12px 4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <span
                              style={{
                                color: "#94a3b8",
                                fontSize: 13,
                                fontWeight: 600,
                                minWidth: 18,
                              }}
                            >
                              {idx + 1}
                            </span>
                            <Text
                              style={{
                                fontWeight: 700,
                                color: "#1e293b",
                                fontSize: 14,
                              }}
                            >
                              {psid}
                            </Text>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Tag
                              style={{
                                borderRadius: 4,
                                fontSize: 11,
                                color: scannedAbsentees.includes(psid)
                                  ? "#0369a1"
                                  : "#64748b",
                                background: scannedAbsentees.includes(psid)
                                  ? "#e0f2fe"
                                  : "#f1f5f9",
                                border: `1px solid ${
                                  scannedAbsentees.includes(psid)
                                    ? "#bae6fd"
                                    : "#e2e8f0"
                                }`,
                                margin: 0,
                              }}
                            >
                              {scannedAbsentees.includes(psid)
                                ? "scanned"
                                : "manual"}
                            </Tag>
                            <button
                              onClick={() => removeAbsentee(psid)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#9ca3af",
                                display: "flex",
                                alignItems: "center",
                                padding: 4,
                                borderRadius: 4,
                              }}
                            >
                              <CloseOutlined style={{ fontSize: 13 }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {verified && (
              <div
                style={{
                  width: "100%",
                  marginTop: 16,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ color: "#d97706", fontSize: 15, flexShrink: 0 }}>
                  ⚠
                </span>
                <Text style={{ fontSize: 13, color: "#92400e" }}>
                  {noAbsentee ? (
                    <>
                      All students will be marked <strong>present</strong>.
                      Review before submitting.
                    </>
                  ) : (
                    <>
                      All students <strong>not</strong> in this list will be
                      auto-marked <strong>present</strong>. Review before
                      submitting.
                    </>
                  )}
                </Text>
              </div>
            )}

            {verified && <div style={{ height: 16 }} />}

            {verified && (
              <button
                onClick={handleSubmitAttendance}
                style={{
                  width: "100%",
                  height: 56,
                  background:
                    "linear-gradient(90deg, #16a34a 0%, #15803d 100%)",
                  border: "none",
                  borderRadius: 12,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: "0 4px 16px rgba(22,163,74,0.3)",
                }}
              >
                <CheckCircleFilled style={{ color: "#fff", fontSize: 18 }} />
                <span
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 16,
                    letterSpacing: "0.02em",
                  }}
                >
                  Submit Attendance
                </span>
              </button>
            )}
          </div>
        )}

        {/* Scanner */}
        <ScannerModal
          open={!!scanTarget}
          onClose={() => setScanTarget(null)}
          onResult={handleScanResult}
          fieldLabel={scannerFieldLabel}
          scannedPsidsRef={scannedAbsenteesRef}
          manualPsidsRef={manualAbsenteesRef}
        />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap');
          * { box-sizing: border-box; }
        `}</style>
      </>
    </ConfigProvider>
  );
};

export default TestAttendance;
